import React, { useState, useMemo } from 'react';
import { debounce } from '../utils/performanceUtils';

const SearchAndFilter = ({ 
    onSearch, 
    onFilter, 
    availableMetrics,
    availableStates,
    onCompare 
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        metric: '',
        operator: 'greater',
        value: '',
        category: 'all'
    });
    const [compareStates, setCompareStates] = useState([]);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [suggestions, setSuggestions] = useState([]);

    // Debounced search with generateSuggestions inside useMemo
    const debouncedSearch = useMemo(
        () => debounce((term) => {
            onSearch(term);
            
            // Generate suggestions inline
            if (!term || term.length < 2) {
                setSuggestions([]);
                return;
            }

            const lowerTerm = term.toLowerCase();
            const newSuggestions = [];

            // Search in states
            availableStates?.forEach(state => {
                if (state.name.toLowerCase().includes(lowerTerm) || 
                    state.code.toLowerCase().includes(lowerTerm)) {
                    newSuggestions.push({
                        type: 'state',
                        value: state.code,
                        display: state.name
                    });
                }
            });

            // Search in metrics
            availableMetrics?.forEach(metric => {
                if (metric.toLowerCase().includes(lowerTerm)) {
                    newSuggestions.push({
                        type: 'metric',
                        value: metric,
                        display: metric
                    });
                }
            });

            setSuggestions(newSuggestions.slice(0, 10));
        }, 300),
        [onSearch, availableStates, availableMetrics]
    );

    // Handle search input
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };


    // Apply filters
    const handleFilterApply = () => {
        const activeFilters = [];
        
        if (filters.metric && filters.value) {
            activeFilters.push({
                metric: filters.metric,
                operator: filters.operator,
                value: parseFloat(filters.value) || filters.value,
                category: filters.category
            });
        }

        onFilter(activeFilters);
    };


    // Remove state from comparison
    const handleRemoveCompareState = (stateCode) => {
        const newStates = compareStates.filter(s => s !== stateCode);
        setCompareStates(newStates);
        onCompare(newStates);
    };

    // Quick filter presets
    const quickFilters = [
        { label: 'Top Income', metric: 'medianHouseholdIncome', operator: 'greater', value: 75000 },
        { label: 'Low Unemployment', metric: 'unemploymentRate', operator: 'less', value: 4 },
        { label: 'High Education', metric: 'educationBachelor', operator: 'greater', value: 35 },
        { label: 'Affordable Housing', metric: 'medianHomeValue', operator: 'less', value: 300000 },
        { label: 'Low Crime', metric: 'violentCrimeRate', operator: 'less', value: 300 }
    ];

    return (
        <div style={styles.container}>
            {/* Search Bar */}
            <div style={styles.searchSection}>
                <div style={styles.searchBar}>
                    <input
                        type="text"
                        placeholder="Search states, metrics, or officials..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        style={styles.searchInput}
                    />
                    <button style={styles.searchButton}>
                        🔍 Search
                    </button>
                </div>
                
                {/* Search Suggestions */}
                {suggestions.length > 0 && (
                    <div style={styles.suggestions}>
                        {suggestions.map((suggestion, index) => (
                            <div
                                key={index}
                                style={styles.suggestionItem}
                                onClick={() => {
                                    setSearchTerm(suggestion.display);
                                    setSuggestions([]);
                                    onSearch(suggestion.value);
                                }}
                            >
                                <span style={styles.suggestionType}>
                                    {suggestion.type === 'state' ? '📍' : '📊'}
                                </span>
                                {suggestion.display}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Quick Filters */}
            <div style={styles.quickFilters}>
                <span style={styles.quickLabel}>Quick Filters:</span>
                {quickFilters.map((filter, index) => (
                    <button
                        key={index}
                        style={styles.quickFilterButton}
                        onClick={() => {
                            setFilters(filter);
                            handleFilterApply();
                        }}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>

            {/* Advanced Filters */}
            <div style={styles.advancedSection}>
                <button
                    style={styles.advancedToggle}
                    onClick={() => setShowAdvanced(!showAdvanced)}
                >
                    {showAdvanced ? '▼' : '▶'} Advanced Filters
                </button>
                
                {showAdvanced && (
                    <div style={styles.advancedFilters}>
                        <div style={styles.filterRow}>
                            <select
                                style={styles.select}
                                value={filters.metric}
                                onChange={(e) => setFilters({...filters, metric: e.target.value})}
                            >
                                <option value="">Select Metric</option>
                                <optgroup label="Economics">
                                    <option value="medianHouseholdIncome">Median Income</option>
                                    <option value="unemploymentRate">Unemployment Rate</option>
                                    <option value="povertyRate">Poverty Rate</option>
                                    <option value="gdpPerCapita">GDP Per Capita</option>
                                </optgroup>
                                <optgroup label="Housing">
                                    <option value="medianHomeValue">Home Value</option>
                                    <option value="medianRent">Rent</option>
                                    <option value="homeownershipRate">Homeownership</option>
                                </optgroup>
                                <optgroup label="Education">
                                    <option value="educationBachelor">Bachelor's Degree</option>
                                    <option value="highSchoolGraduation">HS Graduation</option>
                                </optgroup>
                                <optgroup label="Health">
                                    <option value="lifeExpectancy">Life Expectancy</option>
                                    <option value="uninsuredRate">Uninsured Rate</option>
                                </optgroup>
                                <optgroup label="Crime">
                                    <option value="violentCrimeRate">Violent Crime</option>
                                    <option value="propertyCrimeRate">Property Crime</option>
                                </optgroup>
                            </select>
                            
                            <select
                                style={styles.select}
                                value={filters.operator}
                                onChange={(e) => setFilters({...filters, operator: e.target.value})}
                            >
                                <option value="greater">Greater than</option>
                                <option value="less">Less than</option>
                                <option value="equal">Equal to</option>
                                <option value="between">Between</option>
                            </select>
                            
                            <input
                                type="text"
                                placeholder="Value"
                                style={styles.valueInput}
                                value={filters.value}
                                onChange={(e) => setFilters({...filters, value: e.target.value})}
                            />
                            
                            <button
                                style={styles.applyButton}
                                onClick={handleFilterApply}
                            >
                                Apply Filter
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* State Comparison */}
            {compareStates.length > 0 && (
                <div style={styles.compareSection}>
                    <div style={styles.compareHeader}>
                        <span style={styles.compareLabel}>Comparing States:</span>
                        <button
                            style={styles.clearButton}
                            onClick={() => {
                                setCompareStates([]);
                                onCompare([]);
                            }}
                        >
                            Clear All
                        </button>
                    </div>
                    <div style={styles.compareStates}>
                        {compareStates.map(state => (
                            <div key={state} style={styles.compareStateChip}>
                                {state}
                                <button
                                    style={styles.removeButton}
                                    onClick={() => handleRemoveCompareState(state)}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        padding: '20px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
    },
    searchSection: {
        position: 'relative',
        marginBottom: '16px'
    },
    searchBar: {
        display: 'flex',
        gap: '8px'
    },
    searchInput: {
        flex: 1,
        padding: '12px 16px',
        fontSize: '16px',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    searchButton: {
        padding: '12px 24px',
        backgroundColor: '#002868',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    suggestions: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        marginTop: '4px',
        maxHeight: '300px',
        overflowY: 'auto',
        zIndex: 100,
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    },
    suggestionItem: {
        padding: '10px 16px',
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'background-color 0.2s'
    },
    suggestionType: {
        fontSize: '14px'
    },
    quickFilters: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '16px',
        flexWrap: 'wrap'
    },
    quickLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#666'
    },
    quickFilterButton: {
        padding: '6px 12px',
        backgroundColor: '#f0f0f0',
        border: '1px solid #e0e0e0',
        borderRadius: '20px',
        fontSize: '13px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    advancedSection: {
        marginBottom: '16px'
    },
    advancedToggle: {
        padding: '8px 16px',
        backgroundColor: 'transparent',
        border: 'none',
        fontSize: '14px',
        fontWeight: '600',
        color: '#002868',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    },
    advancedFilters: {
        marginTop: '16px',
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
    },
    filterRow: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    select: {
        padding: '8px 12px',
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        backgroundColor: 'white',
        fontSize: '14px',
        minWidth: '150px'
    },
    valueInput: {
        padding: '8px 12px',
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        fontSize: '14px',
        width: '120px'
    },
    applyButton: {
        padding: '8px 20px',
        backgroundColor: '#002868',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer'
    },
    compareSection: {
        padding: '16px',
        backgroundColor: '#f0f8ff',
        borderRadius: '8px',
        marginTop: '16px'
    },
    compareHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
    },
    compareLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#002868'
    },
    clearButton: {
        padding: '4px 12px',
        backgroundColor: 'transparent',
        border: '1px solid #002868',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#002868',
        cursor: 'pointer'
    },
    compareStates: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap'
    },
    compareStateChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '6px 12px',
        backgroundColor: '#002868',
        color: 'white',
        borderRadius: '20px',
        fontSize: '14px'
    },
    removeButton: {
        backgroundColor: 'transparent',
        border: 'none',
        color: 'white',
        fontSize: '18px',
        cursor: 'pointer',
        padding: 0,
        marginLeft: '4px'
    }
};

export default SearchAndFilter;